import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AiFillAccountBook, AiTwotoneDashboard } from 'react-icons/ai';
import { GrContact } from 'react-icons/gr';
import { GiChest, GiNotebook, GiPersonInBed, GiProgression } from 'react-icons/gi';
import { BsPerson, BsPersonCircle, BsPersonDash, BsPersonFill, BsPersonFillGear, BsPersonFillCheck } from 'react-icons/bs';
import { IoMdLogOut } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { FaHammer, FaPaperPlane, FaCalendarAlt, FaAward, FaCalendarCheck, FaVideo, FaTruck, FaHeartbeat, FaChartBar, FaSearch } from 'react-icons/fa';
import { useAllowedSections } from '../../Routes/customHooks';
import { FaEnvelope, FaQuestionCircle, FaQuoteLeft, FaCommentAlt, FaChevronDown } from 'react-icons/fa';
import { MdAccessibility, MdNature, MdCallMissedOutgoing, MdSettings } from 'react-icons/md';
import { BsShieldLock } from 'react-icons/bs';
import { sectionForPath } from '../../constants/planSections';

// The clinic/user profile page; also reached from the "Staff Profile > Profile" item.
const PROFILE_PATH = '/clinic-profile';

// Menus that have no page built behind them yet (Blogs, Case Studies, Online
// Forms, Landing Pages, Compliance Documents). They resolve to a generic table
// with no data source, so they show an empty list. Flip to false to hide them.
const SHOW_UNBUILT_CONTENT_MENUS = true;

const SidePanel = () => {
  const [isCollapsed, setCollapsed] = useState(false);
  // Groups are open by default; this only tracks the ones the user collapsed.
  const [closedGroups, setClosedGroups] = useState({});
  const location = useLocation();

  const allowedSections = useAllowedSections();
  const loggedInUser = useSelector((state) => state?.AuthReducer?.userLogindata?.data);

  const toggleCollapse = () => {
    setCollapsed(!isCollapsed);
  };

  const links = [
    // Top Core Items
    { id: 'dashboard', text: 'Dashboard', to: '/dashboard', icon: <AiTwotoneDashboard className='h-5 w-5' /> },
    { id: 'appointments', text: 'Appointments', to: '/appointment/list', icon: <GiNotebook className='h-5 w-5' /> },
    { id: 'video_consultations', text: 'Video Consultations', to: '/video-consultation', icon: <FaVideo className='h-5 w-5' /> },
    { id: 'patients', text: 'Patient', to: '/patients', icon: <GiPersonInBed className='h-5 w-5' /> },
    { id: 'vitals_templates', text: 'Vitals Templates', to: '/vitals-templates', icon: <FaHeartbeat className='h-5 w-5' /> },
    { id: 'income_expenses', text: 'Income & Expn', to: '/income_expenses', sections: ['income_expenses', 'referral-business'], icon: <AiFillAccountBook className='h-5 w-5' /> },
    { id: 'inventory', text: 'Inventory', to: '/main/inventory/inventory/addNew', icon: <GiChest className='h-5 w-5' /> },
    { id: 'courier', text: 'Courier', to: '/courier', icon: <FaTruck className='h-5 w-5' /> },

    // Alternative Therapies & Bids Group
    { id: 'alternative_therapies_link', group: 'Alternative Therapies', text: 'Therapy Requests', to: '/AlternativeTherapies', icon: <MdNature className='h-5 w-5' /> },
    { id: 'alternative_therapies_bids', group: 'Alternative Therapies', text: 'Bids', to: '/Bids', icon: <FaHammer className='h-5 w-5' /> },

    // Staff Profile Group
    { id: 'staff_main_doctor', group: 'Staff Profile', text: 'Main Doctor', to: '/main-doctor', roles: ['MainDoctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'], icon: <BsPersonFillCheck className='h-5 w-5' /> },
    { id: 'staff_profile', group: 'Staff Profile', text: 'Staff Profile', to: '/staffprofile', icon: <BsPersonFillGear className='h-5 w-5' /> },
    { id: 'staff_doctors', group: 'Staff Profile', text: 'Doctors', to: '/doctors', icon: <BsPersonFill className='h-5 w-5' /> },
    { id: 'staff_assistant', group: 'Staff Profile', text: 'Assistant Doctor', to: '/assistantDoctor', icon: <BsPersonDash className='h-5 w-5' /> },
    { id: 'staff_receptionist', group: 'Staff Profile', text: 'Receptionist', to: '/receptionist', icon: <BsPerson className='h-5 w-5' /> },
    { id: 'staff_consultant', group: 'Staff Profile', text: 'Consultant', to: '/consultant', icon: <BsPersonCircle className='h-5 w-5' /> },

    // Operations / Attendance Group
    { id: 'ops_attendance_calendar', group: 'Operations', text: 'Attendance Calendar', to: '/attendancecalendar', icon: <FaCalendarCheck className='h-5 w-5' /> },
    { id: 'ops_attendance_count', group: 'Operations', text: 'Mark Attendance & Leave Requests', to: '/attendancecount', icon: <BsPersonFillGear className='h-5 w-5' /> },
    { id: 'ops_leave_calendar', group: 'Operations', text: 'Leave Calendar', to: '/leavecalendar', icon: <FaCalendarAlt className='h-5 w-5' /> },
    { id: 'ops_calendar', group: 'Operations', text: 'Calendar', to: '/calendar', icon: <FaCalendarAlt className='h-5 w-5' /> },
    { id: 'patient_search', text: 'Patient Search', to: '/patient-search', icon: <FaSearch className='h-5 w-5' /> },
    { id: 'ops_certificates',  text: 'Certificates', to: '/certificates', icon: <FaAward className='h-5 w-5' /> },

    // Interactions Group
    { id: 'interact_feedback', group: 'Interactions', text: 'Feedback', to: '/feedback', icon: <FaPaperPlane className='h-5 w-5' /> },
    { id: 'interact_enquiry', group: 'Interactions', text: 'Enquiry', to: '/enquiry', icon: <GrContact className='h-5 w-5' /> },
    { id: 'interact_call_back', group: 'Interactions', text: 'Call Back', to: '/CallBack', icon: <MdCallMissedOutgoing className='h-5 w-5' /> },
    { id: 'interact_questions', group: 'Interactions', text: 'Questions', to: '/Questions', icon: <FaQuestionCircle className='h-5 w-5' /> },
    { id: 'interact_testimonials', group: 'Interactions', text: 'Testimonials', to: '/Testimonials', icon: <FaQuoteLeft className='h-5 w-5' /> },
    { id: 'interact_shared_thoughts', group: 'Interactions', text: 'Shared Thoughts', to: '/SharedThoughts', icon: <FaCommentAlt className='h-5 w-5' /> },
    { id: 'interact_health_packages', group: 'Interactions', text: 'Health Packages', to: '/EnrolledInHealthPackages', icon: <MdAccessibility className='h-5 w-5' /> },
    { id: 'interact_calm_ailment', group: 'Interactions', text: 'Calm Ailment Naturally', to: '/CalmYourEczemaNaturally', icon: <MdNature className='h-5 w-5' /> },
    { id: 'interact_book_therapies', group: 'Interactions', text: 'Book Therapies', to: '/BookAlternativeTherapies', icon: <GiProgression className='h-5 w-5' /> },

    // System & Services Group
    { id: 'service_dropdowns', group: 'Settings', text: 'Drop Downs', to: '/DropDowns', icon: <FaChevronDown className='h-5 w-5' /> },
    { id: 'service_user_profile', group: 'Settings', text: 'User', to: '/login-profile', icon: <BsPersonFillGear className='h-5 w-5' /> },
    { id: 'service_permissions', group: 'Settings', text: 'Permissions', to: '/permissions', roles: ['MainDoctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'], icon: <BsShieldLock className='h-5 w-5' /> },

    // Configuration Group
    { id: 'config_site_config', text: 'Site Config', to: '/site-config', icon: <MdSettings className='h-5 w-5' /> },
    { id: 'config_graphs',  text: 'Graphs', to: '/graphs', icon: <FaChartBar className='h-5 w-5' /> },
  ];

  const canOpenProfile = (() => {
    const section = sectionForPath(PROFILE_PATH);
    return !section || allowedSections.includes(section);
  })();

  const userRoleNormalized = String(loggedInUser?.role || '').toLowerCase().replace(/[_\s-]/g, '');
  const isMainDoctorUser = ['maindoctor', 'admin', 'superadmin', 'masteradmin'].includes(userRoleNormalized);

  const filteredLinks = links.filter((li) => {
    // If role is MainDoctor / admin — show everything!
    if (isMainDoctorUser) return true;
    if (li.unbuilt) return SHOW_UNBUILT_CONTENT_MENUS;
    // `roles` is a hard gate, independent of the plan: the screen behind it
    // refuses other roles anyway, so showing the item would only mislead.
    if (li.roles && !li.roles.includes(loggedInUser?.role)) return false;
    if (
      li.roles &&
      !li.roles.some((r) => r.toLowerCase().replace(/[_\s-]/g, '') === userRoleNormalized)
    ) {
      return false;
    }
    if (li.sections) return li.sections.some((s) => allowedSections.includes(s));
    const section = sectionForPath(li.to);
    if (!section) return true;
    return allowedSections.includes(section);
  });

  useEffect(() => {
    const segment = location.pathname.split('/')[1]?.toLowerCase();
    const active = links.find((l) => l.group && l.to.split('/')[1]?.toLowerCase() === segment);
    if (active) {
      setClosedGroups((prev) => (prev[active.group] ? { ...prev, [active.group]: false } : prev));
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedLinks = (filteredLinks || links).reduce((groups, link) => {
    const groupKey = link.group || 'Ungrouped';
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(link);
    return groups;
  }, {});

  return (
    <aside
      className={`
        SidePanel
        m-3 h-[calc(100vh-1.5rem)]
        sticky top-3 self-start shrink-0
        ${isCollapsed ? 'w-20' : 'w-64'}
        bg-slate-900 text-slate-300
        rounded-2xl shadow-xl border border-slate-800
        p-3
        transition-all duration-300 ease-in-out
        flex flex-col select-none z-30
      `}
    >
      {/* Header & Collapse Toggle */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-2">
        {!isCollapsed && (
          <Link
            to="/dashboard"
            title="Go to Dashboard"
            className="flex items-center gap-2 px-1 py-1 flex-1 min-w-0 rounded-lg hover:bg-slate-800 transition cursor-pointer"
          >
            <div className="w-8 h-8 shrink-0 rounded-lg bg-primary-500/20 text-primary-300 flex items-center justify-center font-bold text-base border border-primary-500/30">
              C
            </div>
            <div className="flex flex-col leading-tight min-w-0 flex-1">
              <span className="font-semibold text-white text-[13px] leading-snug tracking-normal line-clamp-2 break-words">Clinic Management Software</span>
              <span className="text-slate-400 text-[11px] leading-snug tracking-normal truncate">One of India&apos;s Best EMR</span>
            </div>
          </Link>
        )}
        <button
          onClick={toggleCollapse}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-2 shrink-0 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition mx-auto"
        >
          {isCollapsed ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Navigation Links Area */}
      <nav className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
        {Object.entries(groupedLinks).map(([group, groupLinks]) => (
          <div key={group} className="w-full">
            {group === 'Ungrouped' ? (
              <ul className="space-y-1">
                {groupLinks.map((link) => (
                  <li key={link.id}>
                    <NavLink
                      to={link.to}
                      title={isCollapsed ? link.text : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl transition font-medium text-sm ${
                          isActive
                            ? 'bg-primary-500 text-white shadow-md shadow-primary-500/25'
                            : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/80'
                        } ${isCollapsed ? 'justify-center px-2' : ''}`
                      }
                    >
                      <span className="flex-shrink-0">{link.icon}</span>
                      {!isCollapsed && <span className="truncate">{link.text}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="pt-2">
                {!isCollapsed ? (
                  <>
                    <button
                      onClick={() => setClosedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold tracking-wider text-slate-400 uppercase hover:text-slate-200 transition rounded-lg hover:bg-slate-800/50"
                    >
                      <span>{group}</span>
                      <FaChevronDown
                        className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                          !closedGroups[group] ? 'rotate-180 text-primary-300' : ''
                        }`}
                      />
                    </button>
                    {!closedGroups[group] && (
                      <ul className="mt-1 space-y-1 pl-2 border-l border-slate-800/60 ml-2">
                        {groupLinks.map((link) => (
                          <li key={link.id}>
                            <NavLink
                              to={link.to}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg transition text-xs font-medium ${
                                  isActive
                                    ? 'bg-primary-500 text-white shadow-sm'
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                                }`
                              }
                            >
                              <span className="flex-shrink-0">{link.icon}</span>
                              <span className="truncate">{link.text}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <div className="space-y-1 pt-1 border-t border-slate-800/60">
                    {groupLinks.map((link) => (
                      <NavLink
                        key={link.id}
                        to={link.to}
                        title={link.text}
                        className={({ isActive }) =>
                          `flex items-center justify-center p-2 rounded-xl transition ${
                            isActive
                              ? 'bg-primary-500 text-white'
                              : 'text-slate-400 hover:text-white hover:bg-slate-800'
                          }`
                        }
                      >
                        {link.icon}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer / User Profile & Actions */}
      {!isCollapsed ? (
        <div className="pt-3 border-t border-slate-800 mt-2">
          {canOpenProfile ? (
            <NavLink
              to={PROFILE_PATH}
              title="Open clinic profile"
              className={({ isActive }) =>
                `flex items-center gap-3 px-2 py-2 mb-2 rounded-xl transition ${
                  isActive
                    ? 'bg-primary-500/90 text-white shadow-sm shadow-primary-500/25'
                    : 'bg-slate-800/50 hover:bg-slate-800'
                }`
              }
            >
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300">
              <BsPerson className="w-5 h-5" />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-semibold text-white truncate">
                {loggedInUser?.name || 'Administrator'}
              </span>
              <span className="text-[11px] text-slate-400 capitalize truncate">
                {loggedInUser?.role || 'Staff'}
              </span>
            </div>
            </NavLink>
          ) : (
            <div className="flex items-center gap-3 px-2 py-2 mb-2 rounded-xl bg-slate-800/50">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300">
              <BsPerson className="w-5 h-5" />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-semibold text-white truncate">
                {loggedInUser?.name || 'Administrator'}
              </span>
              <span className="text-[11px] text-slate-400 capitalize truncate">
                {loggedInUser?.role || 'Staff'}
              </span>
            </div>
            </div>
          )}

          <button
            onClick={() => {
              window.sessionStorage.clear();
              window.localStorage.removeItem('token');
              window.location.href = '/login';
            }}
            title="Log Out"
            className="w-full mt-1 py-2 rounded-lg text-xs font-medium text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition flex items-center justify-center gap-2"
          >
            <IoMdLogOut className="w-4 h-4" />
            <span>Log Out</span>
          </button>
        </div>
      ) : (
        <div className="pt-2 border-t border-slate-800 flex flex-col items-center gap-2">
          {canOpenProfile && (
            <NavLink
              to={PROFILE_PATH}
              title="Open clinic profile"
              className={({ isActive }) =>
                `p-2 rounded-lg transition ${
                  isActive
                    ? 'bg-primary-500 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`
              }
            >
              <BsPerson className="w-5 h-5" />
            </NavLink>
          )}
          <button
            onClick={() => {
              window.sessionStorage.clear();
              window.localStorage.removeItem('token');
              window.location.href = '/login';
            }}
            title="Log Out"
            className="p-2 rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition"
          >
            <IoMdLogOut className="w-5 h-5" />
          </button>
        </div>
      )}
    </aside>
  );
};

export default SidePanel;
